import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { PaginationModel, ResponseModel } from '../../tools/tool-transfer/tool-transfer.model';
import { AutoCompleteFetchModel, AutoCompleteSelectModel } from './autocomplete.model';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
})
export class AutoCompleteComponent implements OnInit, OnDestroy {
  @ViewChild('searchinput') searchinput: ElementRef;

  @Input() placeholder: string;
  @Input() property: string;
  @Input() extraProperty?: string;
  @Input() label: string;
  @Input() namespace: string;
  @Input() id: string;
  @Input() required?: boolean;

  @Output() onFetch = new EventEmitter<AutoCompleteFetchModel>();
  @Output() onFetchNext = new EventEmitter<AutoCompleteFetchModel>();
  @Output() onSelect = new EventEmitter<AutoCompleteSelectModel>();

  search: string;
  searchSubject = new BehaviorSubject<string>(null);

  searchResults: Array<any> = [];
  searchPagination: PaginationModel;

  searchResultsAreVisible: boolean = false;
  activeIndex: number = 0;
  selectedItems: Array<any> = [];

  searchIgnoreKeys = ['Escape', 'ArrowDown', 'ArrowUp', 'Enter'];

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        filter(value => value !== null),
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe((search: string) => {
        const { id, namespace } = this;
        this.onFetch.emit({ search, id, namespace });
      });
  }

  ngOnDestroy(): void {
    this.searchSubject.unsubscribe();
  }

  setSearch(value: any) {
    this.search = value;
  }

  clearSearch() {
    this.search = '';
  }

  fetchNext(page: number, event: MouseEvent) {
    event.preventDefault();
    const { id, namespace } = this;
    this.onFetchNext.emit({ page, id, namespace });
  }

  fetchSuccessHandler(res: ResponseModel) {
    const { data, pagination } = res;
    this.searchResults = data;
    this.searchPagination = pagination;
    this.showResults();
    this.resetActiveIndex();
  }

  fetchNextSuccessHandler(res: ResponseModel) {
    const { data, pagination } = res;
    this.searchResults = [...this.searchResults, ...data];
    this.searchPagination = pagination;
  }

  handleSearch(event: KeyboardEvent) {
    const { value } = <HTMLInputElement>event.target;
    if (this.searchIgnoreKeys.includes(event.key)) return;
    this.searchSubject.next(value);
  }

  handleSelect(currentIndex: number = -1) {
    if (currentIndex > -1) {
      this.activeIndex = currentIndex;
    }
    const selectedItem = this.searchResults[this.activeIndex];
    const extra = this.extraProperty ? ' - ' + selectedItem[this.extraProperty] : '';
    this.search = selectedItem[this.property] + extra;
    this.onSelect.emit({ item: selectedItem, id: this.id });
    this.hideResults();
  }

  handleTraverseUp() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.scrollIntoView('end');
    }
  }

  handleTraverseDown() {
    if (this.activeIndex < this.searchResults.length - 1) {
      this.activeIndex++;
      this.scrollIntoView('start');
    }
  }

  resetActiveIndex() {
    this.activeIndex = 0;
    this.scrollIntoView('end');
  }

  showResults() {
    if (this.searchResults.length) {
      this.searchResultsAreVisible = true;
    }
  }

  hideResults() {
    this.searchResultsAreVisible = false;
  }

  scrollIntoView(block: ScrollLogicalPosition) {
    const element = document.querySelector(`.result.${this.namespace}.is-active`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block });
    }
  }

  focusSearchInput() {
    this.searchinput.nativeElement.focus();
  }
}
